import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Loader2, Plus, Trash2, GitBranch, ListOrdered, MessageSquare, Info, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StatsSummaryBar from '../../components/StatsSummaryBar';
import ReferencesSection from '../../components/ReferencesSection';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const WORD_ORDER_TYPES = [
  { value: 'SVO', label: 'SVO (Subject-Verb-Object)', description: 'English, French, Chinese' },
  { value: 'SOV', label: 'SOV (Subject-Object-Verb)', description: 'Japanese, Korean, Turkish' },
  { value: 'VSO', label: 'VSO (Verb-Subject-Object)', description: 'Irish, Arabic, Welsh' },
  { value: 'VOS', label: 'VOS (Verb-Object-Subject)', description: 'Malagasy, Fijian' },
  { value: 'OVS', label: 'OVS (Object-Verb-Subject)', description: 'Hixkaryana' },
  { value: 'OSV', label: 'OSV (Object-Subject-Verb)', description: 'Rare' },
  { value: 'free', label: 'Free Word Order', description: 'Latin, Russian (case-marked)' },
];

const CLAUSE_TYPES = [
  { value: 'relative', label: 'Relative Clause' },
  { value: 'conditional', label: 'Conditional Clause' },
  { value: 'temporal', label: 'Temporal Clause' },
  { value: 'causal', label: 'Causal Clause' },
  { value: 'purpose', label: 'Purpose Clause' },
  { value: 'complement', label: 'Complement Clause' },
  { value: 'concessive', label: 'Concessive Clause' },
];

const CLAUSE_POSITIONS = [
  { value: 'before_noun', label: 'Before the noun' },
  { value: 'after_noun', label: 'After the noun' },
  { value: 'sentence_initial', label: 'Sentence initial' },
  { value: 'sentence_final', label: 'Sentence final' },
  { value: 'flexible', label: 'Flexible position' },
];

const SENTENCE_TYPES_OPTIONS = [
  { value: 'interrogative', label: 'Interrogative (Questions)' },
  { value: 'negative', label: 'Negative' },
  { value: 'imperative', label: 'Imperative (Commands)' },
  { value: 'exclamatory', label: 'Exclamatory' },
  { value: 'optative', label: 'Optative (Wishes)' },
];

const SyntaxPage = () => {
  const { conlang, fetchConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  
  const [activeTab, setActiveTab] = useState('word-order');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [wordOrder, setWordOrder] = useState(null);
  const [clauseRules, setClauseRules] = useState([]);
  const [sentenceTypes, setSentenceTypes] = useState([]);
  
  // Word Order form
  const [wordOrderForm, setWordOrderForm] = useState({
    order_type: 'SVO',
    description: '',
    variations: '',
    notes: ''
  });
  const [savingWordOrder, setSavingWordOrder] = useState(false);
  
  // Clause Rule dialog
  const [clauseDialogOpen, setClauseDialogOpen] = useState(false);
  const [clauseForm, setClauseForm] = useState({
    name: '',
    rule_type: 'relative',
    position: 'after_noun',
    marker: '',
    pattern: '',
    examples: '',
    notes: ''
  });
  const [clauseLoading, setClauseLoading] = useState(false);
  
  // Sentence Type dialog
  const [sentenceDialogOpen, setSentenceDialogOpen] = useState(false);
  const [sentenceForm, setSentenceForm] = useState({
    sentence_type: 'interrogative',
    formation: '',
    markers: '',
    word_order_change: '',
    intonation_notes: '',
    examples: '',
    notes: ''
  });
  const [sentenceLoading, setSentenceLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [conlangId]);

  const fetchData = async () => {
    try {
      const [wordOrderRes, clausesRes, sentenceTypesRes] = await Promise.all([
        axios.get(`${API}/conlangs/${conlangId}/word-order`, { withCredentials: true }),
        axios.get(`${API}/conlangs/${conlangId}/clause-rules`, { withCredentials: true }),
        axios.get(`${API}/conlangs/${conlangId}/sentence-types`, { withCredentials: true })
      ]);
      
      if (wordOrderRes.data) {
        setWordOrder(wordOrderRes.data);
        setWordOrderForm({
          order_type: wordOrderRes.data.order_type,
          description: wordOrderRes.data.description || '',
          variations: wordOrderRes.data.variations ? 
            Object.entries(wordOrderRes.data.variations).map(([k, v]) => `${k}: ${v}`).join('\n') : '',
          notes: wordOrderRes.data.notes || ''
        });
      }
      
      setClauseRules(clausesRes.data);
      setSentenceTypes(sentenceTypesRes.data);
    } catch (error) {
      toast.error('Failed to load syntax data');
    } finally {
      setLoading(false);
    }
  };

  const saveWordOrder = async () => {
    setSavingWordOrder(true);
    try {
      // Parse variations from text
      const variationsObj = {};
      if (wordOrderForm.variations) {
        wordOrderForm.variations.split('\n').forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) {
            variationsObj[key] = value;
          }
        });
      }
      
      const response = await axios.post(`${API}/conlangs/${conlangId}/word-order`, {
        order_type: wordOrderForm.order_type,
        description: wordOrderForm.description || null,
        variations: variationsObj,
        notes: wordOrderForm.notes || null
      }, { withCredentials: true });
      
      setWordOrder(response.data);
      toast.success('Word order saved');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to save word order');
    } finally {
      setSavingWordOrder(false);
    }
  };

  const resetClauseForm = () => {
    setClauseForm({
      name: '',
      rule_type: 'relative',
      position: 'after_noun',
      marker: '',
      pattern: '',
      examples: '',
      notes: ''
    });
  };

  const handleAddClause = async (e) => {
    e.preventDefault();
    setClauseLoading(true);
    
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/clause-rules`, {
        name: clauseForm.name,
        rule_type: clauseForm.rule_type,
        position: clauseForm.position,
        marker: clauseForm.marker || null,
        pattern: clauseForm.pattern,
        examples: clauseForm.examples ? clauseForm.examples.split('\n').filter(s => s.trim()) : [],
        notes: clauseForm.notes || null
      }, { withCredentials: true });
      
      setClauseRules([...clauseRules, response.data]);
      setClauseDialogOpen(false);
      resetClauseForm();
      toast.success('Clause rule added');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add clause rule');
    } finally {
      setClauseLoading(false);
    }
  };

  const handleDeleteClause = async (clauseId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/clause-rules/${clauseId}`, {
        withCredentials: true
      });
      setClauseRules(clauseRules.filter(c => c.clause_rule_id !== clauseId));
      toast.success('Clause rule deleted');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to delete clause rule');
    }
  };

  const resetSentenceForm = () => {
    setSentenceForm({
      sentence_type: 'interrogative',
      formation: '',
      markers: '',
      word_order_change: '',
      intonation_notes: '',
      examples: '',
      notes: ''
    });
  };

  const handleAddSentenceType = async (e) => {
    e.preventDefault();
    setSentenceLoading(true);
    
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/sentence-types`, {
        sentence_type: sentenceForm.sentence_type,
        formation: sentenceForm.formation,
        markers: sentenceForm.markers ? sentenceForm.markers.split(',').map(m => m.trim()).filter(m => m) : [],
        word_order_change: sentenceForm.word_order_change || null,
        intonation_notes: sentenceForm.intonation_notes || null,
        examples: sentenceForm.examples ? sentenceForm.examples.split('\n').filter(s => s.trim()) : [],
        notes: sentenceForm.notes || null
      }, { withCredentials: true });
      
      setSentenceTypes([...sentenceTypes, response.data]);
      setSentenceDialogOpen(false);
      resetSentenceForm();
      toast.success('Sentence type added');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add sentence type');
    } finally {
      setSentenceLoading(false);
    }
  };

  const handleDeleteSentenceType = async (typeId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/sentence-types/${typeId}`, {
        withCredentials: true
      });
      setSentenceTypes(sentenceTypes.filter(t => t.sentence_type_id !== typeId));
      toast.success('Sentence type deleted');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to delete sentence type');
    }
  };

  const getClauseTypeColor = (type) => {
    switch (type) {
      case 'relative': return 'text-primary border-primary/50';
      case 'conditional': return 'text-secondary border-secondary/50';
      case 'temporal': return 'text-tertiary border-tertiary/50';
      case 'causal': return 'text-quaternary border-quaternary/50';
      default: return 'text-foreground-muted border-border';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      {/* Stats Summary Bar */}
      <StatsSummaryBar conlang={conlang} conlangId={conlangId} currentSection="syntax" />

      {/* Header */}
      <div>
        <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="syntax-title">
          Syntax
        </h2>
        <p className="text-foreground-muted mt-1">
          Define sentence structure rules for {conlang?.name}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-background-paper border border-border">
          <TabsTrigger value="word-order" data-testid="tab-word-order">
            <ListOrdered className="w-4 h-4 mr-2" />
            Word Order
          </TabsTrigger>
          <TabsTrigger value="clauses" data-testid="tab-clauses">
            <GitBranch className="w-4 h-4 mr-2" />
            Clauses ({clauseRules.length})
          </TabsTrigger>
          <TabsTrigger value="sentence-types" data-testid="tab-sentence-types">
            <MessageSquare className="w-4 h-4 mr-2" />
            Sentence Types ({sentenceTypes.length})
          </TabsTrigger>
        </TabsList>

        {/* Word Order Tab */}
        <TabsContent value="word-order" className="mt-4 space-y-4">
          <Card className="bg-background-paper border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-foreground flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-quaternary" />
                Basic Word Order
              </CardTitle>
              <CardDescription className="text-foreground-muted">
                Define the default order of subject, verb, and object in sentences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">Primary Word Order</Label>
                <Select 
                  value={wordOrderForm.order_type} 
                  onValueChange={(v) => setWordOrderForm({...wordOrderForm, order_type: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="word-order-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {WORD_ORDER_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div>
                          <span className="font-medium">{t.label}</span>
                          <span className="text-foreground-muted text-xs ml-2">({t.description})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-foreground">Description</Label>
                <Textarea
                  value={wordOrderForm.description}
                  onChange={(e) => setWordOrderForm({...wordOrderForm, description: e.target.value})}
                  placeholder="Describe how word order works in your language..."
                  className="bg-background border-border min-h-[80px]"
                  data-testid="word-order-description"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-foreground">Word Order Variations (one per line, format: context: order)</Label>
                <Textarea
                  value={wordOrderForm.variations}
                  onChange={(e) => setWordOrderForm({...wordOrderForm, variations: e.target.value})}
                  placeholder={`questions: VSO
subordinate clauses: SOV
topicalized: OSV`}
                  className="bg-background border-border min-h-[100px] font-mono text-sm"
                  data-testid="word-order-variations"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-foreground">Additional Notes</Label>
                <Textarea
                  value={wordOrderForm.notes}
                  onChange={(e) => setWordOrderForm({...wordOrderForm, notes: e.target.value})}
                  placeholder="Any other notes about word order..."
                  className="bg-background border-border min-h-[60px]"
                  data-testid="word-order-notes"
                />
              </div>
              
              <Button 
                onClick={saveWordOrder}
                disabled={savingWordOrder}
                className="bg-quaternary hover:bg-quaternary/90 text-black"
                data-testid="save-word-order-btn"
              >
                {savingWordOrder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Word Order
              </Button>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="p-4 rounded-md bg-quaternary/10 border border-quaternary/30 flex gap-3">
            <Info className="w-5 h-5 text-quaternary flex-shrink-0 mt-0.5" />
            <div className="text-quaternary text-sm">
              <strong>Word Order</strong> describes the typical arrangement of sentence elements. 
              SVO (Subject-Verb-Object) is common in English: "The cat (S) chased (V) the mouse (O)." 
              SOV languages like Japanese put the verb last: "The cat the mouse chased."
            </div>
          </div>
        </TabsContent>

        {/* Clauses Tab */}
        <TabsContent value="clauses" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-foreground-muted text-sm">
              Define how dependent clauses are formed and connected
            </p>
            <Button 
              onClick={() => setClauseDialogOpen(true)}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="add-clause-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Clause Rule
            </Button>
          </div>

          {clauseRules.length > 0 ? (
            <div className="space-y-4" data-testid="clause-rules-list">
              {clauseRules.map(rule => (
                <Card key={rule.clause_rule_id} className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-heading text-lg text-foreground">{rule.name}</span>
                          <Badge variant="outline" className={getClauseTypeColor(rule.rule_type)}>
                            {rule.rule_type}
                          </Badge>
                          <Badge variant="outline" className="text-foreground-muted border-border text-xs">
                            {rule.position.replace('_', ' ')}
                          </Badge>
                        </div>
                        {rule.marker && (
                          <p className="text-secondary text-sm mb-1">
                            Marker: <span className="font-conlang">{rule.marker}</span>
                          </p>
                        )}
                        <p className="text-foreground-muted text-sm mb-2">{rule.pattern}</p>
                        {rule.examples?.length > 0 && (
                          <div className="text-xs text-foreground-subtle italic">
                            Example: {rule.examples[0]}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteClause(rule.clause_rule_id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-clause-${rule.clause_rule_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-background-paper border-border border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <GitBranch className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  No clause rules yet
                </h3>
                <p className="text-foreground-muted mb-4 max-w-md mx-auto">
                  Define how relative clauses, conditionals, and other dependent clauses work in your language.
                </p>
                <Button onClick={() => setClauseDialogOpen(true)} className="bg-secondary hover:bg-secondary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Clause Rule
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
            <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
            <div className="text-secondary text-sm">
              <strong>Clause Structure</strong> defines how clauses combine. Relative clauses modify nouns 
              ("the man <em>who saw me</em>"). Conditional clauses express hypotheticals ("if it rains..."). 
              Languages differ in where these clauses appear relative to the main clause.
            </div>
          </div>
        </TabsContent>

        {/* Sentence Types Tab */}
        <TabsContent value="sentence-types" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-foreground-muted text-sm">
              Define how questions, negation, commands, and other sentence types are formed
            </p>
            <Button 
              onClick={() => setSentenceDialogOpen(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-sentence-type-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sentence Type
            </Button>
          </div>

          {sentenceTypes.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4" data-testid="sentence-types-list">
              {sentenceTypes.map(type => (
                <Card key={type.sentence_type_id} className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-primary/20 text-primary border-0 capitalize">
                            {type.sentence_type}
                          </Badge>
                        </div>
                        <p className="text-foreground-muted text-sm mb-2">{type.formation}</p>
                        {type.markers?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {type.markers.map((m, idx) => (
                              <span key={idx} className="font-conlang text-xs bg-accent px-2 py-0.5 rounded text-primary">
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                        {type.word_order_change && (
                          <p className="text-xs text-foreground-subtle">
                            Word order: {type.word_order_change}
                          </p>
                        )}
                        {type.examples?.length > 0 && (
                          <p className="text-xs text-foreground-subtle italic mt-1">
                            Ex: {type.examples[0]}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteSentenceType(type.sentence_type_id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-sentence-${type.sentence_type_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-background-paper border-border border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  No sentence types defined
                </h3>
                <p className="text-foreground-muted mb-4 max-w-md mx-auto">
                  Define how questions, negation, commands, and other special sentence types are formed.
                </p>
                <Button onClick={() => setSentenceDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Sentence Type
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-primary text-sm">
              <strong>Sentence Types</strong> cover how different kinds of sentences are formed. 
              Questions might use particles (Japanese "ka"), word order changes (English inversion), 
              or intonation. Negation might use specific words or affixes.
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* References Section */}
      <ReferencesSection section="syntax" />

      {/* Add Clause Rule Dialog */}
      <Dialog open={clauseDialogOpen} onOpenChange={setClauseDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="clause-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Clause Rule</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Define a clause type and how it's formed
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddClause} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Rule Name *</Label>
              <Input
                value={clauseForm.name}
                onChange={(e) => setClauseForm({...clauseForm, name: e.target.value})}
                placeholder="e.g., Relative Clauses with 'who'"
                className="bg-background border-border"
                required
                data-testid="clause-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Clause Type</Label>
                <Select 
                  value={clauseForm.rule_type} 
                  onValueChange={(v) => setClauseForm({...clauseForm, rule_type: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="clause-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {CLAUSE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Position</Label>
                <Select 
                  value={clauseForm.position} 
                  onValueChange={(v) => setClauseForm({...clauseForm, position: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="clause-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {CLAUSE_POSITIONS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Marker/Conjunction (optional)</Label>
              <Input
                value={clauseForm.marker}
                onChange={(e) => setClauseForm({...clauseForm, marker: e.target.value})}
                placeholder="e.g., ke (that), se (if)"
                className="bg-background border-border font-mono"
                data-testid="clause-marker"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Pattern Description *</Label>
              <Textarea
                value={clauseForm.pattern}
                onChange={(e) => setClauseForm({...clauseForm, pattern: e.target.value})}
                placeholder="Describe how this clause type is formed..."
                className="bg-background border-border min-h-[80px]"
                required
                data-testid="clause-pattern"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Examples (one per line)</Label>
              <Textarea
                value={clauseForm.examples}
                onChange={(e) => setClauseForm({...clauseForm, examples: e.target.value})}
                placeholder="talo ke mira = the person who sees"
                className="bg-background border-border min-h-[60px] font-mono text-sm"
                data-testid="clause-examples"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setClauseDialogOpen(false); resetClauseForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={clauseLoading || !clauseForm.name || !clauseForm.pattern} className="bg-secondary hover:bg-secondary/90" data-testid="submit-clause">
                {clauseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Clause Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Sentence Type Dialog */}
      <Dialog open={sentenceDialogOpen} onOpenChange={setSentenceDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sentence-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Sentence Type</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Define how a sentence type is formed
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSentenceType} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Sentence Type</Label>
              <Select 
                value={sentenceForm.sentence_type} 
                onValueChange={(v) => setSentenceForm({...sentenceForm, sentence_type: v})}
              >
                <SelectTrigger className="bg-background border-border" data-testid="sentence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  {SENTENCE_TYPES_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">How is it formed? *</Label>
              <Textarea
                value={sentenceForm.formation}
                onChange={(e) => setSentenceForm({...sentenceForm, formation: e.target.value})}
                placeholder="Describe how this sentence type is constructed..."
                className="bg-background border-border min-h-[80px]"
                required
                data-testid="sentence-formation"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Markers/Particles (comma-separated)</Label>
              <Input
                value={sentenceForm.markers}
                onChange={(e) => setSentenceForm({...sentenceForm, markers: e.target.value})}
                placeholder="e.g., ka, ne, ma (question particles)"
                className="bg-background border-border font-mono"
                data-testid="sentence-markers"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Word Order Change (optional)</Label>
              <Input
                value={sentenceForm.word_order_change}
                onChange={(e) => setSentenceForm({...sentenceForm, word_order_change: e.target.value})}
                placeholder="e.g., VSO for questions"
                className="bg-background border-border"
                data-testid="sentence-word-order"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Intonation Notes (optional)</Label>
              <Input
                value={sentenceForm.intonation_notes}
                onChange={(e) => setSentenceForm({...sentenceForm, intonation_notes: e.target.value})}
                placeholder="e.g., Rising intonation at end"
                className="bg-background border-border"
                data-testid="sentence-intonation"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Examples (one per line)</Label>
              <Textarea
                value={sentenceForm.examples}
                onChange={(e) => setSentenceForm({...sentenceForm, examples: e.target.value})}
                placeholder="katal mira ka? = Do you see the ocean?"
                className="bg-background border-border min-h-[60px] font-mono text-sm"
                data-testid="sentence-examples"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setSentenceDialogOpen(false); resetSentenceForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={sentenceLoading || !sentenceForm.formation} className="bg-primary hover:bg-primary/90" data-testid="submit-sentence-type">
                {sentenceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Sentence Type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SyntaxPage;
